/**
 * PricingRuleConditionsWidget — combines the two low-traffic field groups the
 * generic renderer used to show as separate cards ("Period Settings" → Company,
 * "Party Information" → Selling / Buying) into ONE collapsible section, collapsed
 * by default like the Advanced disclosure, kept at the BOTTOM of the page.
 *
 * Everything stays editable (staged via onFieldChange; the page's Save persists).
 */

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";

const pick = (th: boolean, t: string, e: string) => (th ? t : e);

export function PricingRuleConditionsWidget({ formData, onFieldChange, disabled, isSaving }: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const th = language === "th";
  const ro = disabled || isSaving;
  const [open, setOpen] = useState(false);

  return (
    <div className="px-5 pt-6 pb-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <SectionHeader label={pick(th, "การตั้งค่าเพิ่มเติม", "More settings")} />
        <ChevronDown className={cn("mb-2 h-4 w-4 shrink-0 text-gray-400 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="overflow-clip rounded-xl border border-border bg-card shadow-sm">
          <Row label={pick(th, "ร้านค้า / บริษัท", "Company")}>
            <div className="w-56">
              <DocTypeSearchAutocomplete
                doctype="Company"
                value={(formData.company as string) || ""}
                onChange={(v) => onFieldChange("company", v)}
                placeholder={pick(th, "เลือกบริษัท…", "Select company…")}
                minSearchLength={0}
                disabled={ro}
              />
            </div>
          </Row>
          <Divider />
          <Row label={pick(th, "ใช้กับการขาย", "Selling")}>
            <Switch checked={!!formData.selling} onCheckedChange={(c) => onFieldChange("selling", c ? 1 : 0)} disabled={ro} />
          </Row>
          <Divider />
          <Row label={pick(th, "ใช้กับการซื้อ", "Buying")}>
            <Switch checked={!!formData.buying} onCheckedChange={(c) => onFieldChange("buying", c ? 1 : 0)} disabled={ro} />
          </Row>
        </div>
      )}
    </div>
  );
}

/** Inline row (label left, control right) matching the generic inline-field rows. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-5 px-5 py-4">
      <span className="shrink-0 text-sm text-gray-900 dark:text-gray-100">{label}</span>
      <div className="flex min-w-0 items-center justify-end">{children}</div>
    </div>
  );
}

function Divider() {
  return <div className="mx-5 border-t border-border/40" />;
}
